import { CSSObject } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { SLIDE_CLASS } from "../types";

export const cellCSS: CSSObject = {
  position: "relative",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

export const header: CSSObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 16px",
  height: "24px"
};

export const picker: CSSObject = {
  background: colors.white,
  border: `1px solid ${colors.gray200}`,
  borderRadius: "5px",
  width: "324px",
  position: "absolute",
  left: 0,
  zIndex: 100,
  boxShadow:
    "0 6px 14px -6px rgba(24, 39, 75, 0.12), 0 10px 32px -4px rgba(24, 39, 75, 0.10)"
};

export const value: CSSObject = {
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
};

export const bubble = ({
  isSelected,
  isInRange,
  isToday,
  isStart,
  isEnd,
  isDisabled,
  isPreviewEnd,
  disableHoverOnSameDay
}: {
  isSelected: boolean;
  isInRange: boolean;
  isToday: boolean;
  isStart?: boolean;
  isEnd?: boolean;
  isDisabled: boolean;
  isPreviewEnd?: boolean;
  disableHoverOnSameDay?: boolean;
}): CSSObject => {
  const hoverBorderRadius = () => {
    if (isInRange) return 0;
    if (isStart && !isEnd) return "20px 0 0 20px";
    if (isEnd && !isStart) return "0 20px 20px 0";
    return "20px";
  };

  const getBorderRadius = () => {
    if (isPreviewEnd) return 0;
    if (isStart && !isEnd) return "20px 0 0 20px";
    if (isEnd && !isStart) return "0 20px 20px 0";
    return "20px";
  };

  return {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: getBorderRadius(),
    color: isInRange ? colors.white : colors.textDark,
    cursor: isDisabled || disableHoverOnSameDay ? "auto" : "pointer",
    userSelect: "none",

    ...(isDisabled && {
      background: "transparent",
      color: colors.textDisabled
    }),
    ...(isPreviewEnd && {
      background: colors.gray100
    }),
    ...(isSelected &&
      !isInRange && {
        background: colors.blue700,
        color: colors.white,
        "&:hover": {
          background: disableHoverOnSameDay ? colors.blue700 : colors.blue800,
          borderRadius: hoverBorderRadius()
        }
      }),
    ...(isInRange && {
      background: colors.blue700,
      "&:hover": {
        background: colors.blue800,
        borderRadius: hoverBorderRadius()
      }
    }),
    ...(isToday &&
      !isPreviewEnd && {
        boxShadow: `inset 0 0 0 1px ${colors.gray300}`,
        ...(!isDisabled && {
          "&:hover": {
            background: colors.blue800,
            borderRadius: hoverBorderRadius()
          }
        })
      }),
    ...(!isSelected &&
      !isInRange &&
      !isDisabled &&
      !isPreviewEnd && {
        "&:hover": { background: colors.gray100 }
      })
  };
};

export const yearBubble = ({
  isSelected,
  isInRange,
  isToday,
  isDisabled
}: {
  isSelected: boolean;
  isInRange: boolean;
  isToday: boolean;
  isDisabled: boolean;
}): CSSObject => ({
  ...bubble({ isSelected, isInRange, isToday, isDisabled })
});

export const slideAnimations = `
  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(-48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes slideOutLeft {
    from { opacity: 1; transform: translateX(0); }
    to   { opacity: 0; transform: translateX(-48px); }
  }
  @keyframes slideOutRight {
    from { opacity: 1; transform: translateX(0); }
    to   { opacity: 0; transform: translateX(48px); }
  }
`;

export const getSlideStyle = (slideClass: SLIDE_CLASS): React.CSSProperties => {
  const animationMap: Record<SLIDE_CLASS, React.CSSProperties> = {
    [SLIDE_CLASS.Idle]: {},
    [SLIDE_CLASS.ExitLeft]: { animation: "slideOutLeft 140ms ease forwards" },
    [SLIDE_CLASS.ExitRight]: { animation: "slideOutRight 140ms ease forwards" },
    [SLIDE_CLASS.EnterLeft]: { animation: "slideInLeft 180ms ease forwards" },
    [SLIDE_CLASS.EnterRight]: { animation: "slideInRight 180ms ease forwards" }
  };
  return animationMap[slideClass];
};

export const yearGrid: CSSObject = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gridAutoRows: "32px",
  rowGap: "8px",
  height: "152px",
  overflowY: "auto",
  overflowX: "hidden",
  contain: "strict",
  paddingTop: "16px"
};

export const monthGrid: CSSObject = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gridAutoRows: "32px",
  rowGap: "8px",
  height: "152px",
  overflowY: "auto",
  paddingTop: "16px"
};

export const rootCSS = (disabled?: boolean): CSSObject => ({
  width: "100%",
  cursor: disabled ? "auto" : "pointer"
});

export const input = (
  open: boolean,
  disabled?: boolean,
  smallerInput?: boolean
): CSSObject => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  width: "100%",
  overflow: "hidden",
  height: smallerInput ? undefined : "44px",
  padding: smallerInput ? "5px 12px" : "10px 12px",
  border: `1px solid ${colors.gray300}`,
  borderRadius: "6px",
  background: colors.white,
  boxSizing: "border-box",
  "&:hover": { borderColor: colors.gray400 },

  ...(disabled && {
    background: colors.gray100,
    pointerEvents: "none"
  }),

  ...(open &&
    !disabled && {
      borderColor: colors.blue700,
      boxShadow: `0 0 0 2px ${colors.blue200}`
    })
});

export const cell: CSSObject = {
  position: "relative",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

export const headerCSS: CSSObject = {
  cursor: "pointer",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  gap: "4px"
};

export const expandMoreStyle = (shouldTransform: boolean) => ({
  width: "16px",
  height: "16px",
  transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1)",
  transform: shouldTransform ? "rotate(0deg)" : "rotate(180deg)"
});
